<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import Drawer from 'primevue/drawer';
import { CustomButton, ButtonType } from '@/components/common/';
import type { PerfusionScanModel } from '@/models/perfusionScan';
import { usePerfusionScanStore } from '@/stores/perfusionScan';
import PerfusionScanList from './PerfusionScanList.vue';
import PerfusionScanDetailDialog from './PerfusionScanDetailDialog.vue';

const props = defineProps({
  visible: {
    type: Boolean,
    required: true
  },
  /** The investigation the perfusion belongs to — `:id` on the companion endpoints. */
  pumpCenterId: {
    type: String,
    required: true
  },
  /** The case whose scans are listed — `:perfusionId` on the companion endpoints. */
  perfusionId: {
    type: String,
    required: true
  },
  perfusionLabel: {
    type: String,
    default: ''
  }
});

const emit = defineEmits(['onClose']);

const perfusionScanStore = usePerfusionScanStore();

const selectedScan = ref<PerfusionScanModel | undefined>();
const showDetailDialog = ref(false);

const panelTitle = computed(() =>
  props.perfusionLabel ? `Scans - ${props.perfusionLabel}` : 'Perfusion scans'
);

const drawerStyle = computed(() => ({ width: 'min(440px, 80vw)' }));

const canLoad = computed(() => !!props.pumpCenterId && !!props.perfusionId);

const scanCountText = computed(() => {
  const total = perfusionScanStore.total;
  if (perfusionScanStore.scansLoader.isLoading || total === 0) {
    return '';
  }

  const loaded = perfusionScanStore.scans.length;
  const suffix = total === 1 ? 'scan' : 'scans';
  // The endpoint pages, so say so rather than implying the list is everything.
  return loaded < total ? `Showing ${loaded} of ${total} ${suffix}` : `${total} ${suffix}`;
});

const loadScans = () => {
  if (!canLoad.value) {
    return;
  }

  return perfusionScanStore.getScans(props.pumpCenterId, props.perfusionId);
};

const setDetailDialogVisible = (open: boolean) => {
  showDetailDialog.value = open;
  if (!open) {
    // Dropped with the dialog, so nothing can reopen onto the scan that was last viewed.
    selectedScan.value = undefined;
  }
};

watch(
  () => [props.visible, props.perfusionId] as const,
  ([isVisible]) => {
    // The detail dialog is a sibling of the drawer rather than a child of it, so closing the panel
    // or switching case has to take it down explicitly — otherwise it is left on screen over a
    // panel that is gone, showing a scan that may belong to another case.
    setDetailDialogVisible(false);

    if (isVisible) {
      void loadScans();
    } else {
      perfusionScanStore.clearScans();
    }
  },
  { immediate: true }
);

const handleClose = () => {
  emit('onClose');
};

const openScan = (scan: PerfusionScanModel) => {
  selectedScan.value = scan;
  showDetailDialog.value = true;
};
</script>

<template>
  <Drawer v-if="visible"
    :visible="visible"
    position="right"
    :header="panelTitle"
    class="scans-drawer"
    :style="drawerStyle"
    @update:visible="handleClose">
    <div class="panel-actions">
      <span class="scans-count">{{ scanCountText }}</span>
      <CustomButton text="Refresh"
        :type="ButtonType.OUTLINED"
        :icon="{ icon: 'arrow-rotate-right', style: 'far' }"
        :loading="perfusionScanStore.scansLoader.isLoading"
        @onClick="loadScans"/>
    </div>

    <PerfusionScanList :scans="perfusionScanStore.scans"
      :loading="perfusionScanStore.scansLoader.isLoading"
      :pump-center-id="pumpCenterId"
      :perfusion-id="perfusionId"
      @select="openScan"/>
  </Drawer>

  <PerfusionScanDetailDialog :visible="showDetailDialog"
    :scan="selectedScan"
    :pump-center-id="pumpCenterId"
    :perfusion-id="perfusionId"
    :perfusion-label="perfusionLabel"
    @update:visible="setDetailDialogVisible"/>
</template>

<style scoped lang="scss">
.panel-actions {
  padding: 0 0 16px;
  border-bottom: 1px solid var(--color-border);
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
}

.scans-count {
  font-size: 12px;
  color: #6b7280;
}

.scans-drawer :deep(.p-drawer-content) {
  display: flex;
  flex-direction: column;
}
</style>
